/* eslint-disable @next/next/no-img-element -- The original landing footer depends on intrinsic source image sizing. */

import { LandingLink as Link } from './landing-link';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { PiStarBold } from 'react-icons/pi';
import { LandingNewsletterForm } from './landing-controls';
import './landing-source-footer.css';

const columns = [
  {
    title: 'Shippers',
    links: [
      ['Managed Transportation', '/shippers/managed-transportation'],
      ['Freight Brokerage', '/shippers/freight-brokerage'],
      ['Dedicated Capacity', '/shippers/dedicated-capacity'],
      ['Warehousing', '/shippers/warehousing'],
      ['Expedited Freight', '/services/expedited'],
    ],
  },
  {
    title: 'Carriers',
    links: [
      ['Join Our Network', '/carriers/become-a-carrier'],
      ['Reefer Opportunities', '/services/reefer'],
      ['Dry Van Opportunities', '/services/dry-van'],
      ['Flatbed & Open-Deck', '/services/flatbed'],
      ['Dedicated Lanes', '/carriers/available-loads'],
    ],
  },
  {
    title: 'Solutions',
    links: [
      ['Technology & Visibility', '/company/shipment-visibility'],
      ['Specialized Freight', '/shippers/project-logistics'],
      ['Project Logistics', '/shippers/project-logistics'],
      ['Cross-Border Logistics', '/shippers/international-logistics'],
      ['Safety & Compliance', '/company/carrier-network'],
    ],
  },
  {
    title: 'Company',
    links: [
      ['About Us', '/company/about'],
      ['Leadership', '/company/about'],
      ['Careers', '/company/about'],
      ['News & Insights', '/company/news'],
      ['Contact Us', '/get-quote'],
    ],
  },
] as const;

const socialLinks = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/shipsmartsolutions/people/',
    Icon: FaLinkedinIn,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/people/Ship-Smart-Solutions-INC/61580481933361/',
    Icon: FaFacebookF,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/ship.smart.solutions/',
    Icon: FaInstagram,
  },
  {
    label: 'Clutch',
    href: 'https://clutch.co/profile/ship-smart-solutions',
    Icon: PiStarBold,
  },
] as const;

/**
 * The footer CTA header changes per page in the source design, only this block does; the
 * link columns and contact details below it are the same everywhere. Defaults are the home
 * page's copy, so callers that do not care can keep rendering <LandingSourceFooter />.
 */
export type LandingFooterCta = {
  pill: string;
  title: string;
  copy: string;
  action: string;
};

export const defaultFooterCta: LandingFooterCta = {
  pill: 'Ready to move?',
  title: 'Let\u2019s move your business forward',
  copy: 'Tell us about your freight needs. Our logistics team will get back to you within one business day.',
  action: 'Get started',
};

export function LandingSourceFooter({
  cta = defaultFooterCta,
  showCta = true,
}: {
  cta?: LandingFooterCta;
  showCta?: boolean;
} = {}) {
  return (
    <footer className="footer-12 changeless overflow-hidden">
      {showCta ? (
        <div className="footer-12__cta" id="contact">
          <div className="container-fluid">
            <div className="footer-12__cta-inner">
              <span
                className="footer-12__cta-pill at_fade_anim"
                data-fade-from="bottom"
                data-delay=".05"
              >
                <span className="footer-12__cta-pill-dot" aria-hidden="true" />
                <span className="footer-12__cta-pill-text">{cta.pill}</span>
              </span>
              <h2 className="footer-12__cta-title reveal-text mb-0">
                <span className="d-block">{cta.title}</span>
              </h2>
              <p
                className="footer-12__cta-sub at_fade_anim mb-0"
                data-fade-from="bottom"
                data-delay=".15"
              >
                {cta.copy}
              </p>
              <LandingNewsletterForm action={cta.action} />
            </div>
          </div>
        </div>
      ) : null}

      <div className="footer-12__main">
        <div className="container-fluid">
          <span className="footer-12__divider" aria-hidden="true" />
          <div className="footer-12__columns">
            <div className="footer-12__col footer-12__col--brand">
              <Link
                className="footer-12__brand"
                href="/"
                aria-label="Ship Smart Solutions home"
              >
                <img
                  className="footer-12__brand-logo"
                  src="/landing/assets/images/shipsmart/logo.png"
                  alt="Ship Smart Solutions"
                />
              </Link>
              <p className="footer-12__brand-desc">
                Reliable freight solutions built around speed, visibility, and
                dependable capacity. We help businesses move freight efficiently
                across the United States and beyond.
              </p>
              <p className="footer-12__brand-desc">
                SmartWay Transport Partner · ID 16218902 · valid through
                September 16, 2026.
              </p>
              <div
                className="at-hero-social footer-12__socials"
                aria-label="Social links"
              >
                {socialLinks.map(({ label, href, Icon }) => (
                  <a
                    href={href}
                    key={label}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {label}
                    <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            </div>

            {columns.map((column) => (
              <div className="footer-12__col" key={column.title}>
                <p className="footer-12__col-title">{column.title}</p>
                <ul className="footer-12__col-links">
                  {column.links.map(([label, href]) => (
                    <li key={label}>
                      <Link href={href}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="footer-12__contacts pb-40">
            {[
              ['Phone:', '(331)-249-2786'],
              ['Email:', 'operations@ship-solutions.net'],
              ['Address:', '650 E Diehl Rd, Naperville, IL 60563'],
              ['Operations:', '24/7'],
            ].map(([label, value]) => (
              <div className="footer-12__contact" key={label}>
                <div className="footer-12__col footer-12__col--hours">
                  <p className="footer-12__col-title">{label}</p>
                  <p className="footer-12__hours-value mb-0">{value}</p>
                </div>
              </div>
            ))}
          </div>

          <span className="footer-12__divider" aria-hidden="true" />
          <div className="footer-12__bottom">
            <p className="footer-12__copy mb-0">
              © 2026 Ship Smart Solutions. All rights reserved.
            </p>
            <ul className="footer-12__legal" aria-label="Legal">
              <li>
                <Link href="/terms">Terms</Link>
              </li>
              <li>
                <Link href="/freight-terms">Freight Terms</Link>
              </li>
              <li>
                <Link href="/privacy">Privacy</Link>
              </li>
              <li>
                <Link href="/privacy#cookies-and-similar-technologies">
                  Cookies
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
